// Meditations weighting + helpers.
//
// A "meditation" is Dan's reflection on a piece — text written from /meditations.
// Pieces accrue weight from cheap signals (timing, tag richness, color count,
// has-meditation, mark-for-meditation, recency) so the recommended sort surfaces
// interesting work without needing a real recommender.

export function meditationScore(order, ctx = {}) {
  const { shiftAvgDrawMs } = ctx;
  let s = 0;

  // Curation signals — strong
  if (order.meditation && order.meditation.trim()) s += 4;
  if (order.meditationMark) s += 5;
  if (order.publicGallery) s += 1;

  // Drawing time vs shift average
  if (shiftAvgDrawMs && order.drawingTimeMs) {
    if (order.drawingTimeMs < shiftAvgDrawMs * 0.85) s += 2;
    else if (order.drawingTimeMs < shiftAvgDrawMs) s += 1;
  }

  // Tag richness (vibes + AI tags)
  const tagCount = (order.artistTags?.length || 0) + (order.artistTagsReflection?.length || 0) + (order.ai_tags?.length || 0);
  if (tagCount >= 4) s += 2;
  else if (tagCount >= 2) s += 1;

  // Color richness
  const colorCount = order.favoriteColors?.length || 0;
  if (colorCount >= 3) s += 1;

  // Recency taper — full credit ≤7d, fades to 0 over 90d
  const ts = order.completedAt ?? order.photographedAt ?? order.createdAt;
  if (ts?.toMillis) {
    const ageDays = (Date.now() - ts.toMillis()) / 86400000;
    if (ageDays <= 7) s += 1;
    else if (ageDays <= 30) s += 0.5;
    else if (ageDays > 365) s -= 0.5;
  }

  // Photo recency — separate from order completion; favors pancakes photographed
  // in the last week so Dan's staring at fresh work rather than months-old
  // pieces he's already seen a thousand times.
  const photoTs = order.photographedAt ?? order.readyToDeliverAt;
  if (photoTs?.toMillis) {
    const photoAgeDays = (Date.now() - photoTs.toMillis()) / 86400000;
    if (photoAgeDays <= 7)  s += 2;
    else if (photoAgeDays <= 14) s += 1;
    else if (photoAgeDays <= 30) s += 0.3;
  }

  return s;
}

// Picks N pieces with weighted random sampling (no replacement). Pinned
// piece(s) always come first.
export function sampleMeditations(orders, n = 3, ctx = {}) {
  const pinned = orders.filter((o) => o.meditationPinned);
  const pool   = orders.filter((o) => !o.meditationPinned);

  const weighted = pool.map((o) => ({ o, w: Math.max(0.1, meditationScore(o, ctx)) }));
  const picked = [];
  const want = Math.max(0, n - pinned.length);

  for (let i = 0; i < want && weighted.length > 0; i++) {
    const total = weighted.reduce((a, b) => a + b.w, 0);
    let r = Math.random() * total;
    let idx = 0;
    for (; idx < weighted.length; idx++) {
      r -= weighted[idx].w;
      if (r <= 0) break;
    }
    if (idx >= weighted.length) idx = weighted.length - 1;
    picked.push(weighted[idx].o);
    weighted.splice(idx, 1);
  }

  return [...pinned, ...picked];
}

export function meditationCaption(order) {
  if (order.meditation && order.meditation.trim()) return order.meditation.trim();
  return computeDisplayTitle(order) || order.subjectName || order.recipientName || order.notes?.slice(0, 80) || "Untitled pancake";
}

// Auto-generated title, stamped from the per-design lifetime counter
// (config/designCounters). Returns null for titled customs or pre-counter
// legacy orders — caller falls back to the manual subjectName.
//   Flash:            "Saturnina (#6)"
//   Freestyle:        "Freestyle (#7) — Fire Truck"
//   Untitled custom:  "Untitled #45" (guest skipped the title field; the
//                     "untitled" counter key numbers them like a design)
export function computeDisplayTitle(order) {
  if (!order) return null;
  const seq = order.designSequence;
  if (!seq) return null;
  const isFreestyle = order.orderType === "freestyle_flash" || order.isFreestyle;
  if (isFreestyle) {
    const subject = (order.freestyleDescription || order.subjectName || order.notes || "").trim();
    return subject ? `Freestyle (#${seq}) — ${subject}` : `Freestyle (#${seq})`;
  }
  if (order.orderType === "flash" && order.designLabel) {
    return `${order.designLabel} (#${seq})`;
  }
  if (order.orderType === "custom" && !(order.subjectName || "").trim()) {
    return `Untitled #${seq}`;
  }
  return null;
}

export function shortLabel(order) {
  return computeDisplayTitle(order) || order.subjectName || order.recipientName || order.customerName || (order.queueNumber ? `#${order.queueNumber}` : "Untitled");
}
